import React, { useState, useEffect, useMemo } from 'react';
import {
  Button, Typography, Box, Card, CardContent, TextField, CircularProgress,
  Link, Accordion, AccordionSummary, AccordionDetails, Grid, Divider,
  Tabs, Tab, Chip, LinearProgress, Tooltip, Paper,
} from '@material-ui/core';
import { Avatar } from '@material-ui/core';
import {
  ExpandMore, CheckCircle, Cancel, Warning, Android, PhoneIphone,
  Search, ClearAll, TableChart, ViewModule,
} from '@material-ui/icons';
import MUIDataTable from 'mui-datatables';
import AppAnalyser3 from './AppAnalyser3';

function TabPanel({ children, value, index }) {
  return value === index ? <Box pt={2}>{children}</Box> : null;
}

// ---------------------------------------------------------------------------
// Scoring helpers
// ---------------------------------------------------------------------------

function getAppScoreAndApproval(appData, monetizationData, options = {}) {
  const {
    minReviews = 10,
    blockSensitive = true,
    blockUtility = true,
    sensitiveKeywords = [
      'bdsm','erotic','porn','vpn','launcher','wallpaper','keyboard','emoji','sticker','adblock','theme',
      'battery booster','lockscreen','ringtones','calculator','widget','cleaner','flashlight','alarm clock',
      'screensaver','timer','timers','stopwatch','stopwatches','shortcut','shortcuts','caller','calling',
      'block spam','file','files','pdf reader','pdf scan','pdf scanner','file manager','equalizer','gallery',
      'real money','real cash','real rewards','cash out','get paid','payout','notepad','mirror','browser',
      'cast','sms skin','calendar','qr','barcode','screen mirroring','cloud storage','earn free','earn rewards',
      'earn cash','free bitcoin','redeem for money','money earning','withdraw','withdrawal','locker','calc',
      'bang','nude','wallpapers','photo gallery','remote','battery','booster','xlsx',
    ],
    sensitiveGameKeywords = ['bdsm','erotic','erotik','fuck','hentai','lesbian','naughty','porn','pussy','rape','xtube','nude','real money','real cash'],
    reviewScoreRanges = [
      { min: 10,     max: 9999,    score: 15 },
      { min: 10000,  max: 149999,  score: 25 },
      { min: 150000, max: Infinity, score: 35 },
    ],
    ratingScoreRanges = [
      { min: 3,    max: 3.74, score: 15 },
      { min: 3.75, max: 4.49, score: 25 },
      { min: 4.5,  max: 5,    score: 35 },
    ],
    utilityGenres   = ['Utilities', 'Personalization', 'Tools'],
    knownGameGenres = ['action','adventure','arcade','board','card','casino','casual','educational','music',
      'puzzle','racing','role playing','simulation','sports','strategy','trivia','word','game','tower defense'],
  } = options;

  const isGame   = g => g && knownGameGenres.some(k => g.toLowerCase().includes(k));
  const revScore = n => { for (const r of reviewScoreRanges) if (n >= r.min && n <= r.max) return r.score; return 0; };
  const ratScore = n => { for (const r of ratingScoreRanges) if (n >= r.min && n <= r.max) return r.score; return 0; };
  const hasSens  = (name, list) => {
    const lower = (name || '').toLowerCase();
    const words = new Set(lower.split(/\s+/));
    return list.filter(k => !k.includes(' ')).some(k => words.has(k))
        || list.filter(k =>  k.includes(' ')).some(k => lower.includes(k));
  };

  const result = { ...appData };
  result.isBlocked       = Boolean(monetizationData?.is_blocked);
  result.isMonetized     = Boolean(monetizationData?.network_count > 0);
  result.monetizedBy     = monetizationData?.network_count ?? 0;
  result.is_autoapproved = Boolean(monetizationData?.is_autoapproved);

  if (result.isBlocked)       return { ...result, score: 0,   approvalStatus: 'Not Approved', reason: 'Blocked bundle' };
  if (result.is_autoapproved) return { ...result, score: 100, approvalStatus: 'Approved',     reason: 'Autoapproved bundle' };

  if (blockUtility && utilityGenres.includes(result.primaryGenre))
    return { ...result, score: 0, approvalStatus: 'Not Approved', reason: 'Sensitive category' };

  let score    = 5;
  const gameApp = isGame(result.primaryGenre);
  const kwList  = gameApp ? sensitiveGameKeywords : sensitiveKeywords;
  if (blockSensitive && hasSens(result.name, kwList))
    return { ...result, score: 0, approvalStatus: 'Not Approved', reason: `Sensitive keyword (${gameApp ? 'Game' : 'App'})` };

  score += 5;
  const revCount = parseInt(result.reviewCount) || 0;
  score += revScore(revCount);
  score += ratScore(parseFloat(result.averageRating) || 0);
  const nets = monetizationData?.network_count ?? 0;
  score += nets > 10 ? 15 : nets > 0 ? 5 : 0;

  if (result.found === false)    return { ...result, score: 'N/A', approvalStatus: 'Not Approved', reason: 'Not found' };
  if (revCount < minReviews)     return { ...result, score: 'N/A', approvalStatus: 'Manual check',  reason: 'Too few reviews to score' };

  const status = score < 40 ? 'Not Approved' : score < 55 ? 'Manual check' : 'Approved';
  const reason = score < 40 ? 'Score < 40'   : score < 55 ? 'Score 40–54'  : 'Score ≥ 55';
  return { ...result, score, approvalStatus: status, reason };
}

function processAndroidAppData(data, id) {
  const apps = data.apps ? JSON.parse(data.apps) : [];
  if (!apps.length || apps[0].name === 'No name') return { id, platform: 'Android', found: false };
  const app = apps[0];
  const reviewCount = parseInt((String(app.reviewCount || '')).replace(/,| /g, '')) || 0;
  const ratings = [app.rating, app.ratingES, app.ratingFR, app.ratingDE, app.ratingPL, app.ratingIT].map(r => parseFloat(r) || 0);
  const valid   = ratings.filter(r => r > 0);
  const avg     = valid.length ? parseFloat((valid.reduce((a, x) => a + x, 0) / valid.length).toFixed(2)) : 0;
  return {
    id, platform: 'Android', found: true,
    name: app.name || 'N/A', bundleId: id, averageRating: avg,
    averageCountryRating: ratings[0],
    ratingES: ratings[1], ratingFR: ratings[2], ratingDE: ratings[3], ratingPL: ratings[4], ratingIT: ratings[5],
    reviewCount, developerPage: app.developerPage || '',
    primaryGenre: app.genre || '',
    appStoreUrl: app.shopurl || `https://play.google.com/store/apps/details?id=${id}`,
    iconUrl: app.imageUrl || '', fullData: app,
  };
}

async function fetchMonetizationBatch(ids) {
  if (!ids?.length) return {};
  const resp = await fetch('https://mobileappscrapper.herokuapp.com/isMonetizedBatch', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ inventory: ids }),
  });
  const data = await resp.json();
  const map = {};
  data.forEach(item => { map[item.name] = item; });
  return map;
}

async function batchAllMonetization(ids, batchSize = 200) {
  let map = {};
  for (let i = 0; i < ids.length; i += batchSize) {
    const result = await fetchMonetizationBatch(ids.slice(i, i + batchSize));
    map = { ...map, ...result };
  }
  return map;
}

// ---------------------------------------------------------------------------
// App Analyser 2.0 component
// ---------------------------------------------------------------------------

const STATUS_COLORS = {
  'Approved':     { bg: '#27ae60', text: '#fff' },
  'Not Approved': { bg: '#e74c3c', text: '#fff' },
  'Manual check': { bg: '#f39c12', text: '#fff' },
};
const StatusChip = ({ value }) => {
  const c = STATUS_COLORS[value] || { bg: '#95a5a6', text: '#fff' };
  return (
    <Chip
      label={value || '—'}
      size="small"
      style={{ backgroundColor: c.bg, color: c.text, fontWeight: 700, fontSize: 11 }}
    />
  );
};
const ScoreChip = ({ value }) => {
  if (value === 'N/A' || value === undefined) return <Chip label="N/A" size="small" style={{ background: '#bdc3c7', color: '#fff', fontWeight: 700 }} />;
  const bg = value >= 55 ? '#27ae60' : value >= 40 ? '#f39c12' : '#e74c3c';
  return <Chip label={value} size="small" style={{ backgroundColor: bg, color: '#fff', fontWeight: 700, minWidth: 40 }} />;
};
const PlatformChip = ({ value }) => {
  const isIOS = value === 'iOS';
  return (
    <Chip
      icon={isIOS ? <PhoneIphone style={{ fontSize: 14 }} /> : <Android style={{ fontSize: 14 }} />}
      label={value}
      size="small"
      style={{
        backgroundColor: isIOS ? '#1c1c1e' : '#3ddc84',
        color: isIOS ? '#fff' : '#000',
        fontWeight: 600, fontSize: 11,
      }}
    />
  );
};

function StatCard({ label, value, color, icon }) {
  return (
    <Paper elevation={0} style={{
      padding: '12px 20px', borderRadius: 10, textAlign: 'center',
      background: `linear-gradient(135deg, ${color}22, ${color}11)`,
      border: `1px solid ${color}44`, minWidth: 100,
    }}>
      <Typography style={{ fontSize: 26, fontWeight: 800, color, lineHeight: 1 }}>{value}</Typography>
      <Typography variant="caption" style={{ color: '#666', fontWeight: 600 }}>{label}</Typography>
    </Paper>
  );
}

function AppAnalyzer() {
  const [appIds, setAppIds]   = useState('');
  const [results, setResults] = useState(() => {
    try { const s = localStorage.getItem('appAnalyzerResults'); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  const [loading, setLoading]   = useState(false);
  const [loadingStep, setLoadingStep] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [minReviews, setMinReviews] = useState(10);
  const [sensitiveKeywords, setSensitiveKeywords] = useState(
    'bdsm, erotic, porn, vpn, launcher, wallpaper, keyboard, emoji, sticker, adblock, theme, battery booster, lockscreen, ringtones, calculator, widget, cleaner, flashlight, alarm clock, screensaver'
  );
  const [reviewScoreRanges] = useState([
    { min: 10, max: 9999, score: 15 },
    { min: 10000, max: 149999, score: 25 },
    { min: 150000, max: Infinity, score: 35 },
  ]);
  const [ratingScoreRanges] = useState([
    { min: 3, max: 3.74, score: 15 },
    { min: 3.75, max: 4.49, score: 25 },
    { min: 4.5, max: 5, score: 35 },
  ]);

  const SENSITIVE_KEYWORDS = useMemo(
    () => sensitiveKeywords.split(',').map(k => k.trim().toLowerCase()).filter(Boolean),
    [sensitiveKeywords]
  );

  useEffect(() => {
    if (results.length > 0) {
      try { localStorage.setItem('appAnalyzerResults', JSON.stringify(results)); }
      catch { localStorage.removeItem('appAnalyzerResults'); }
    }
  }, [results]);

  const ids = useMemo(
    () => appIds.split(/\n|,/).map(id => id.trim()).filter(Boolean),
    [appIds]
  );

  async function handleAnalyze() {
    setResults([]);
    localStorage.removeItem('appAnalyzerResults');
    if (!ids.length) return;
    setLoading(true);

    const iosIds     = ids.filter(id => /^\d+$/.test(id));
    const androidIds = ids.filter(id => !/^\d+$/.test(id));
    const allResults = [];

    if (iosIds.length) {
      setLoadingStep(`Fetching ${iosIds.length} iOS app${iosIds.length > 1 ? 's' : ''} from iTunes…`);
      for (let i = 0; i < iosIds.length; i += 100) {
        const batch = iosIds.slice(i, i + 100);
        try {
          const resp = await fetch(`https://itunes.apple.com/lookup?id=${batch.join(',')}`);
          const data = await resp.json();
          batch.forEach(id => {
            const found = data.results.find(a => String(a.trackId) === id);
            allResults.push({
              id, platform: 'iOS', found: !!found,
              name: found?.trackCensoredName || '',
              averageRating: found?.averageUserRating || 0,
              reviewCount: found?.userRatingCount || 0,
              primaryGenre: found?.primaryGenreName || '',
              appStoreUrl: found?.trackViewUrl || '',
              iconUrl: found?.artworkUrl100 || '',
              fullData: found || null,
            });
          });
        } catch {
          batch.forEach(id => allResults.push({ id, platform: 'iOS', found: false }));
        }
      }
    }

    if (androidIds.length) {
      setLoadingStep(`Fetching ${androidIds.length} Android app${androidIds.length > 1 ? 's' : ''} from Play Store…`);
      for (const id of androidIds) {
        try {
          const resp = await fetch(`https://mobileappscrapper.herokuapp.com/html2/${id}`);
          const data = await resp.json();
          allResults.push(processAndroidAppData(data, id));
        } catch {
          allResults.push({ id, platform: 'Android', found: false });
        }
      }
    }

    setLoadingStep(`Checking monetization for ${allResults.length} apps…`);
    const monetizationMap = await batchAllMonetization(allResults.map(x => x.id));

    setLoadingStep('Scoring…');
    setResults(allResults.map(app =>
      getAppScoreAndApproval(app, monetizationMap[app.id], {
        minReviews, sensitiveKeywords: SENSITIVE_KEYWORDS,
        reviewScoreRanges, ratingScoreRanges,
      })
    ));
    setLoadingStep('');
    setLoading(false);
  }

  // Summary stats
  const stats = useMemo(() => ({
    total:      results.length,
    approved:   results.filter(r => r.approvalStatus === 'Approved').length,
    manual:     results.filter(r => r.approvalStatus === 'Manual check').length,
    rejected:   results.filter(r => r.approvalStatus === 'Not Approved').length,
    ios:        results.filter(r => r.platform === 'iOS').length,
    android:    results.filter(r => r.platform === 'Android').length,
  }), [results]);

  const columns = [
    {
      name: 'icon', label: 'Icon',
      options: { filter: false, sort: false, customBodyRender: v => v
        ? <Avatar variant="rounded" src={v} style={{ width: 36, height: 36, borderRadius: 8 }} />
        : <Avatar variant="rounded" style={{ width: 36, height: 36, borderRadius: 8, background: '#eee' }}>?</Avatar>
      }
    },
    { name: 'id',   label: 'App ID',  options: { filter: false } },
    { name: 'name', label: 'Name' },
    {
      name: 'platform', label: 'Platform',
      options: { customBodyRender: v => <PlatformChip value={v} /> }
    },
    { name: 'averageRating', label: '⭐ Rating' },
    { name: 'reviewCount',   label: 'Reviews' },
    {
      name: 'appStoreUrl', label: 'Store',
      options: { filter: false, sort: false, customBodyRender: v => v
        ? <Link href={v} target="_blank" rel="noopener" style={{ fontWeight: 600, color: '#fe5000' }}>Open ↗</Link>
        : '—'
      }
    },
    {
      name: 'developerPage', label: 'Developer',
      options: { filter: false, sort: false, customBodyRender: v => v
        ? <Link href={v} target="_blank" rel="noopener" style={{ fontWeight: 600, color: '#fe5000' }}>Open ↗</Link>
        : '—'
      }
    },
    {
      name: 'isBlocked', label: 'Blocked',
      options: { customBodyRender: v => v
        ? <Chip label="Blocked" size="small" style={{ background: '#e74c3c', color: '#fff', fontWeight: 700 }} />
        : <Chip label="No" size="small" style={{ background: '#ecf0f1', color: '#666' }} />
      }
    },
    {
      name: 'isMonetized', label: 'Monetized',
      options: { customBodyRender: v => v
        ? <Chip label="Yes" size="small" style={{ background: '#27ae60', color: '#fff', fontWeight: 700 }} />
        : <Chip label="No" size="small" style={{ background: '#ecf0f1', color: '#666' }} />
      }
    },
    { name: 'monetizedBy', label: 'Networks' },
    { name: 'ratingDE', label: 'DE' }, { name: 'ratingES', label: 'ES' },
    { name: 'ratingFR', label: 'FR' }, { name: 'ratingIT', label: 'IT' }, { name: 'ratingPL', label: 'PL' },
    {
      name: 'approvalStatus', label: 'Status',
      options: { customBodyRender: v => <StatusChip value={v} /> }
    },
    { name: 'reason', label: 'Reason' },
    {
      name: 'score', label: 'Score',
      options: { customBodyRender: v => <ScoreChip value={v} /> }
    },
  ];

  const tableData = results.map(r => ({
    icon: r.iconUrl, id: r.id, name: r.name || '—',
    appStoreUrl: r.appStoreUrl, platform: r.platform,
    averageRating: r.averageRating ? Number(r.averageRating).toFixed(2) : '—',
    reviewCount: r.reviewCount?.toLocaleString() || '—',
    developerPage: r.developerPage, isBlocked: r.isBlocked, isMonetized: r.isMonetized,
    monetizedBy: r.monetizedBy || 0,
    ratingDE: r.ratingDE || '—', ratingES: r.ratingES || '—',
    ratingFR: r.ratingFR || '—', ratingIT: r.ratingIT || '—', ratingPL: r.ratingPL || '—',
    approvalStatus: r.approvalStatus, reason: r.reason, score: r.score,
  }));

  return (
    <Box p={1}>
      {/* ── Input card ── */}
      <Card style={{ marginBottom: 16, borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={1} style={{ gap: 10 }}>
            <Search style={{ color: '#fe5000' }} />
            <Typography variant="h6" style={{ fontWeight: 700 }}>App Analyser 2.0</Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
            Paste App IDs separated by new lines or commas. <strong>Numeric IDs</strong> = iOS · <strong>Bundle names</strong> = Android
          </Typography>
          <TextField
            variant="outlined" multiline rows={5} fullWidth
            value={appIds}
            onChange={e => setAppIds(e.target.value)}
            placeholder={"123456789\ncom.example.app\n987654321"}
            inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
          />
          <Box mt={2} display="flex" alignItems="center" style={{ gap: 10 }}>
            <Button
              variant="contained" color="primary"
              onClick={handleAnalyze}
              disabled={loading || !ids.length}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Search />}
              style={{ borderRadius: 8, padding: '8px 24px', fontWeight: 700, fontSize: 14 }}
            >
              {loading ? 'Analysing…' : `Analyse ${ids.length ? `${ids.length} app${ids.length > 1 ? 's' : ''}` : ''}`}
            </Button>
            {results.length > 0 && (
              <Tooltip title="Clear results">
                <Button
                  variant="outlined" size="small"
                  onClick={() => { setResults([]); localStorage.removeItem('appAnalyzerResults'); }}
                  disabled={loading}
                  startIcon={<ClearAll />}
                  style={{ borderRadius: 8 }}
                >
                  Clear
                </Button>
              </Tooltip>
            )}
            {results.length > 0 && (
              <Box display="flex" style={{ marginLeft: 'auto', gap: 6 }}>
                <Tooltip title="Table view">
                  <Button
                    size="small" variant={viewMode === 'table' ? 'contained' : 'outlined'}
                    color={viewMode === 'table' ? 'primary' : 'default'}
                    onClick={() => setViewMode('table')}
                    style={{ minWidth: 40, padding: '6px 10px', borderRadius: 8 }}
                  >
                    <TableChart fontSize="small" />
                  </Button>
                </Tooltip>
                <Tooltip title="Card view">
                  <Button
                    size="small" variant={viewMode === 'cards' ? 'contained' : 'outlined'}
                    color={viewMode === 'cards' ? 'primary' : 'default'}
                    onClick={() => setViewMode('cards')}
                    style={{ minWidth: 40, padding: '6px 10px', borderRadius: 8 }}
                  >
                    <ViewModule fontSize="small" />
                  </Button>
                </Tooltip>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* ── Loading state ── */}
      {loading && (
        <Box mb={2}>
          <LinearProgress color="secondary" style={{ borderRadius: 4, height: 6 }} />
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 6, fontStyle: 'italic' }}>
            {loadingStep}
          </Typography>
        </Box>
      )}

      {/* ── Summary stats ── */}
      {!loading && results.length > 0 && (
        <Box display="flex" flexWrap="wrap" style={{ gap: 10, marginBottom: 16 }}>
          <StatCard label="Total" value={stats.total} color="#636e72" />
          <StatCard label="Approved" value={stats.approved} color="#27ae60" />
          <StatCard label="Manual Check" value={stats.manual} color="#f39c12" />
          <StatCard label="Not Approved" value={stats.rejected} color="#e74c3c" />
          <Box style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
            <StatCard label="iOS" value={stats.ios} color="#1c1c1e" />
            <StatCard label="Android" value={stats.android} color="#3ddc84" />
          </Box>
        </Box>
      )}

      {/* ── Table view ── */}
      {!loading && results.length > 0 && viewMode === 'table' && (
        <MUIDataTable
          title={`${results.length} apps analysed`}
          data={tableData}
          columns={columns}
          options={{
            filterType: 'checkbox', selectableRows: 'none', responsive: 'standard',
            rowsPerPage: 25,
            downloadOptions: { filename: `AppAnalysis_${new Date().toISOString().slice(0, 10)}.csv` },
          }}
        />
      )}

      {/* ── Card view ── */}
      {!loading && results.length > 0 && viewMode === 'cards' && (
        <Grid container spacing={2}>
          {results.map((r, i) => {
            const statusColor = STATUS_COLORS[r.approvalStatus] || { bg: '#95a5a6', text: '#fff' };
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                <Card style={{
                  borderRadius: 12, height: '100%',
                  borderTop: `3px solid ${statusColor.bg}`,
                  boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                  display: 'flex', flexDirection: 'column',
                }}>
                  <CardContent style={{ flexGrow: 1 }}>
                    {/* Header row */}
                    <Box display="flex" alignItems="flex-start" mb={1} style={{ gap: 10 }}>
                      {r.iconUrl
                        ? <img src={r.fullData?.artworkUrl100 || r.iconUrl} alt={r.name}
                            style={{ width: 48, height: 48, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }} />
                        : <Box style={{ width: 48, height: 48, borderRadius: 10, background: '#f0f0f0', flexShrink: 0 }} />
                      }
                      <Box style={{ overflow: 'hidden' }}>
                        <Typography variant="subtitle1" style={{ fontWeight: 700, lineHeight: 1.2, marginBottom: 2 }} noWrap>
                          {r.name || '—'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" noWrap>{r.id}</Typography>
                      </Box>
                    </Box>

                    {/* Chips row */}
                    <Box display="flex" flexWrap="wrap" style={{ gap: 4, marginBottom: 10 }}>
                      <PlatformChip value={r.platform} />
                      <StatusChip value={r.approvalStatus} />
                      <ScoreChip value={r.score} />
                    </Box>

                    <Divider style={{ marginBottom: 8 }} />

                    {/* Stats */}
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="caption" color="textSecondary">Rating</Typography>
                      <Typography variant="caption" style={{ fontWeight: 600 }}>
                        ⭐ {r.averageRating ? Number(r.averageRating).toFixed(2) : '—'}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="caption" color="textSecondary">Reviews</Typography>
                      <Typography variant="caption" style={{ fontWeight: 600 }}>
                        {r.reviewCount?.toLocaleString() || '—'}
                      </Typography>
                    </Box>
                    {r.primaryGenre && (
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" color="textSecondary">Genre</Typography>
                        <Typography variant="caption" style={{ fontWeight: 600 }}>{r.primaryGenre}</Typography>
                      </Box>
                    )}
                    {r.reason && (
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="caption" color="textSecondary">Reason</Typography>
                        <Typography variant="caption" style={{ fontWeight: 600, color: statusColor.bg, textAlign: 'right', maxWidth: '60%' }}>
                          {r.reason}
                        </Typography>
                      </Box>
                    )}

                    {/* Description accordion for iOS */}
                    {r.platform === 'iOS' && r.fullData?.description && (
                      <Accordion style={{ marginTop: 8, boxShadow: 'none', border: '1px solid #eee', borderRadius: 8 }}>
                        <AccordionSummary expandIcon={<ExpandMore />} style={{ minHeight: 36, padding: '0 10px' }}>
                          <Typography variant="caption" style={{ fontWeight: 600 }}>Description</Typography>
                        </AccordionSummary>
                        <AccordionDetails style={{ padding: '0 10px 10px' }}>
                          <Typography variant="caption" style={{ whiteSpace: 'pre-wrap', color: '#555' }}>
                            {r.fullData.description.slice(0, 250)}{r.fullData.description.length > 250 ? '…' : ''}
                          </Typography>
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </CardContent>

                  {/* Footer links */}
                  {(r.appStoreUrl || r.developerPage) && (
                    <Box
                      px={2} pb={1.5}
                      display="flex" style={{ gap: 12 }}
                      borderTop="1px solid #f0f0f0"
                      pt={1}
                    >
                      {r.appStoreUrl && (
                        <Link href={r.appStoreUrl} target="_blank" rel="noopener"
                          style={{ fontSize: 12, fontWeight: 600, color: '#fe5000' }}>
                          Store ↗
                        </Link>
                      )}
                      {r.developerPage && (
                        <Link href={r.developerPage} target="_blank" rel="noopener"
                          style={{ fontSize: 12, fontWeight: 600, color: '#636e72' }}>
                          Developer ↗
                        </Link>
                      )}
                    </Box>
                  )}
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main — 2-tab container
// ---------------------------------------------------------------------------

export default function AppAnalyserBeta() {
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        indicatorColor="secondary"
        textColor="secondary"
        style={{ borderBottom: '1px solid #e0e0e0', marginBottom: 8 }}
      >
        <Tab label="App Analyser 2.0" />
        <Tab label="Batch Analyser" />
      </Tabs>

      <TabPanel value={tab} index={0}>
        <AppAnalyzer />
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <AppAnalyser3 />
      </TabPanel>
    </Box>
  );
}
